import { useMutation, useQuery } from '@tanstack/react-query';
import { CreateTagRequest, GetTagsListResponse, UpdateTagRequest } from 'compliance-api';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_FORM_QUERY_KEY } from '@/modules/common/queries/forms.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_USER_FORMS_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/forms.ts';

export const TAGS_LIST_QK = 'tags-list';

export const useGetFormTagsListQuery = (disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetTagsListResponse>({
    queryKey: [TAGS_LIST_QK, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTagsListV1FormsTagsGet();
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreateFormTagMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateTagRequest) => {
      const response = await withErrorHandling(api.v1.createTagV1FormsTagsPost(payload), {
        loading: 'Creating category...',
        success: 'Category created',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([TAGS_LIST_QK]);
    },
  });
};

export const useUpdateFormTagMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateTagRequest) => {
      const response = await withErrorHandling(api.v1.updateTagV1FormsTagsPatch(payload), {
        loading: 'Updating category...',
        success: 'Category updated',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_FORM_QUERY_KEY]);
      queryClient.invalidateQueries([TAGS_LIST_QK]);
    },
  });
};

export const useDeleteFormTagMutation = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(api.v1.deleteTagV1FormsTagsIdDelete({ id }), {
        loading: 'Deleting tag...',
        success: 'Tag deleted',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_FORM_QUERY_KEY]);
      queryClient.invalidateQueries([TAGS_LIST_QK]);
    },
  });
};
